import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  Settings
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
  const { user, signOut, isAdmin, refreshAdminStatus } = useAuth();
  const location = useLocation();
  const profileRef = useRef<HTMLDivElement>(null);

  // Admin status is managed by AuthContext - no need to refresh on every navigation
  // This useEffect has been removed to prevent excessive admin status polling

  const toggleMenu = () => setIsOpen(!isOpen);

  // Handle profile menu toggling with proper event stopping
  const toggleProfileMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsProfileOpen(!isProfileOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      setIsProfileOpen(false);
      console.log('Navbar: Sign out initiated');
      
      // Close mobile menu if open
      if (isOpen) {
        setIsOpen(false);
      }
      
     // Add loading state feedback
     toast.info('Signing out...');
     
      await signOut();
    } catch (error) {
      console.error('Navbar: Error during sign out:', error);
      toast.error('Error signing out');
     
     // Force navigation on error
     setTimeout(() => {
       window.location.href = '/';
     }, 500);
    }
  };

  // Admin app URL (from environment or default)
  const adminAppUrl = import.meta.env.VITE_ADMIN_APP_URL || '/admin/login';

  // Log admin status for debugging
  useEffect(() => {
    console.log('🔍 Navbar: Current admin status for', user?.email, ':', isAdmin);
    console.log('💾 Navbar: Stored admin status in localStorage:', localStorage.getItem('career_lens_admin_status'));
  }, [isAdmin]);

  const navLinks = [
    { label: 'Home', path: '/', active: location.pathname === '/' },
  ];

  const userNavLinks = [
    { label: 'Dashboard', path: '/dashboard', active: location.pathname === '/dashboard' },
    { label: 'Reports', path: '/reports', active: location.pathname === '/reports' },
    { label: 'Skills Tracker', path: '/skills-tracker', active: location.pathname === '/skills-tracker' },
    { label: 'AI Coach', path: '/ai-coach', active: location.pathname === '/ai-coach' },
    { label: 'Job Match AI', path: '/job-match', active: location.pathname === '/job-match' },
  ];

  const adminNavLinks = [
    { label: 'Admin', path: '/admin', active: location.pathname.startsWith('/admin') },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-28 md:h-36 items-center">
        <Link to="/" className="flex items-center font-medium">
          <img
            src="/future lens pro logo.png"
            alt="FutureLens Pro"
            className="h-24 md:h-32 w-auto"
          />
        </Link>
        
        <nav className="ml-auto hidden md:flex gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                link.active ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          {user ? (
            <>
              {userNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    link.active ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {isAdmin && adminNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    link.active ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={toggleProfileMenu}
                  className="flex items-center text-sm font-medium gap-1 hover:text-primary"
                >
                  Profile
                  <ChevronDown className="h-4 w-4" />
                </button>
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm">Signed in as</p>
                      <p className="text-sm font-medium truncate">{user.email}</p>
                      {isAdmin && (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded">
                          <Settings className="h-3 w-3" />
                          Admin Access
                        </div>
                      )}
                    </div>
                    
                    <Link 
                      to="/profile" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" 
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2 text-gray-500" />
                      Account Settings
                    </Link>
                    
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-2 text-gray-500" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex gap-4">
              <Link to="/login">
                <Button variant="ghost">Log in</Button>
              </Link>
              <Link to="/signup">
                <Button>Sign up</Button>
              </Link>
            </div>
          )}
        </nav>
        
        <button
          className="ml-auto md:hidden"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
        
        {isOpen && (
          <div className="absolute top-28 left-0 right-0 p-4 bg-background border-b border-border/40 md:hidden z-50">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    link.active ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {user ? (
                <>
                  {userNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm font-medium transition-colors hover:text-primary ${
                        link.active ? 'text-primary' : 'text-muted-foreground'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}

                  {isAdmin && adminNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm font-medium transition-colors hover:text-primary ${
                        link.active ? 'text-primary' : 'text-muted-foreground'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  
                  <button
                    onClick={handleSignOut}
                    className="text-sm font-medium text-left text-destructive hover:text-destructive/80 flex items-center"
                    data-testid="sign-out-button-mobile"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" className="w-full justify-center">Log in</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsOpen(false)}>
                    <Button className="w-full justify-center">Sign up</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;